import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Clock,
  User,
  Activity,
  TrendingUp,
  Target,
  ShieldAlert,
  AlertTriangle,
  MessageSquare
} from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import { useGetSessionDetailQuery } from '@/features/admin/api/sessionsApi';

function StatBox({ label, value, sub, icon: Icon, color }: { label: string; value: string; sub?: string; icon: any; color: string }) {
  return (
    <div style={{
      padding: '16px',
      borderRadius: '0',
      border: '1px solid var(--admin-card-border)',
      background: 'var(--admin-card-bg)',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--admin-card-subtext)', letterSpacing: '0.05em' }}>{label}</span>
        <div style={{ width: '28px', height: '28px', borderRadius: '0', background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
          <Icon size={14} />
        </div>
      </div>
      <div>
        <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", color: 'var(--admin-card-text)' }}>{value}</div>
        {sub && <div style={{ fontSize: '11px', color: 'var(--admin-card-subtext)', marginTop: '2px', fontWeight: 500 }}>{sub}</div>}
      </div>
    </div>
  );
}

import { SessionAnalyticsSkeleton } from '@/features/admin/components/loaders/SessionAnalyticsSkeleton';

export function SessionAnalytics() {
  const { id: sessionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'transcript'>('overview');

  useEffect(() => {
    const mainBody = document.querySelector('.admin-main-body');
    if (mainBody) {
      mainBody.scrollTop = 0;
    }
  }, [sessionId]);

  const { data, isLoading, isFetching, error } = useGetSessionDetailQuery(sessionId as string, {
    skip: !sessionId,
  });

  const session = data?.data;

  // Prepare chart data
  const sentimentData = session?.sentimentSnapshots?.map((s, idx) => ({
    time: `T+${idx}`,
    score: s.score,
    label: s.label
  })) || [];

  const engagementData = session?.engagementSnapshots?.map((s, idx) => ({
    time: `T+${idx}`,
    score: s.score,
    label: s.label
  })) || [];

  if (isLoading || isFetching) {
    return <SessionAnalyticsSkeleton onBack={() => navigate(-1)} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}
    >
      {/* Header with Back Button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '8px',
            borderRadius: '0',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            color: 'var(--admin-card-text)',
            border: '1px solid var(--admin-card-border)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            color: 'var(--admin-card-text)',
            fontSize: '37.9px',
            fontWeight: 700,
            margin: '0 0 4px',
            letterSpacing: '-0.76px',
            lineHeight: '38px'
          }}>
            {session?.meetingTitle || 'Call Analysis Details'}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', fontWeight: 500, color: '#7C8891', fontFamily: "'Inter', sans-serif" }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={13} /> {session?.durationSeconds ? `${Math.round(session.durationSeconds / 60)}m` : '-'}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><User size={13} /> {session?.platform || 'Unknown'}</span>
            <span>{session?.startedAt ? new Date(session.startedAt).toLocaleString() : ''}</span>
          </div>
        </div>
      </div>

      <div style={{ background: 'var(--admin-card-bg)', borderRadius: '0', border: '1px solid var(--admin-card-border)', display: 'flex', flexDirection: 'column' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '0 20px', borderBottom: '1px solid var(--admin-card-border)' }}>
          {(['overview', 'transcript'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '14px 0',
                fontSize: '13px',
                fontWeight: 700,
                fontFamily: "'Space Grotesk', sans-serif",
                textTransform: 'capitalize',
                color: activeTab === tab ? '#38bdf8' : 'var(--admin-card-subtext)',
                position: 'relative',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                borderRadius: '0'
              }}
            >
              {tab}
              {activeTab === tab && (
                <motion.div
                  layoutId="activeTabSession"
                  style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '2px', background: '#38bdf8' }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {error ? (
            <div style={{ padding: '14px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '0', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AlertTriangle size={16} />
              Failed to load session details.
            </div>
          ) : activeTab === 'overview' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Top Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                <StatBox label="Final Sentiment" value={session?.finalSentimentScore?.toFixed(2) || 'N/A'} sub={session?.sentimentSnapshots?.[session.sentimentSnapshots.length - 1]?.label || ''} icon={TrendingUp} color="#10B981" />
                <StatBox label="Final Engagement" value={session?.finalEngagementScore?.toFixed(2) || 'N/A'} sub={session?.engagementSnapshots?.[session.engagementSnapshots.length - 1]?.label || ''} icon={Activity} color="#8B5CF6" />
                <StatBox label="Buying Intent" value={session?.finalBuyingIntentLabel || 'N/A'} sub={session?.finalBuyingIntentScore ? `${(session.finalBuyingIntentScore * 100).toFixed(0)}% confidence` : ''} icon={Target} color="#F59E0B" />
                <StatBox label="Battlecards Used" value={session?.battlecardsUsed?.toString() || '0'} sub={`out of ${session?.battlecardsGenerated || 0} generated`} icon={ShieldAlert} color="#38BDF8" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                {/* Sentiment Chart */}
                <div style={{ padding: '16px', borderRadius: '0', border: '1px solid var(--admin-card-border)', background: 'var(--admin-card-bg)' }}>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '14px', fontWeight: 700, marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--admin-card-text)' }}>
                    <TrendingUp size={15} color="#10B981" /> Sentiment Trend
                  </h3>
                  <div style={{ height: '220px', width: '100%' }}>
                    {sentimentData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={sentimentData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--admin-card-border)" />
                          <XAxis dataKey="time" hide />
                          <YAxis domain={[-1, 1]} tick={{ fontSize: 10, fill: 'var(--admin-card-subtext)' }} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{ borderRadius: '0', border: '1px solid var(--admin-card-border)', backgroundColor: 'var(--admin-card-bg)', color: 'var(--admin-card-text)' }} />
                          <Area type="monotone" dataKey="score" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorScore)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', opacity: 0.5, color: 'var(--admin-card-subtext)' }}>No sentiment data</div>
                    )}
                  </div>
                </div>

                {/* Engagement Chart */}
                <div style={{ padding: '16px', borderRadius: '0', border: '1px solid var(--admin-card-border)', background: 'var(--admin-card-bg)' }}>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '14px', fontWeight: 700, marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--admin-card-text)' }}>
                    <Activity size={15} color="#8B5CF6" /> Engagement Dynamics
                  </h3>
                  <div style={{ height: '220px', width: '100%' }}>
                    {engagementData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={engagementData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorEngagement" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--admin-card-border)" />
                          <XAxis dataKey="time" hide />
                          <YAxis domain={[0, 1]} tick={{ fontSize: 10, fill: 'var(--admin-card-subtext)' }} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{ borderRadius: '0', border: '1px solid var(--admin-card-border)', backgroundColor: 'var(--admin-card-bg)', color: 'var(--admin-card-text)' }} />
                          <Area type="monotone" dataKey="score" stroke="#8B5CF6" strokeWidth={2} fillOpacity={1} fill="url(#colorEngagement)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', opacity: 0.5, color: 'var(--admin-card-subtext)' }}>No engagement data</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Buying Intent Signals & Objections */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                <div style={{ padding: '16px', borderRadius: '0', border: '1px solid var(--admin-card-border)', background: 'var(--admin-card-bg)' }}>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '14px', fontWeight: 700, marginBottom: '12px', color: 'var(--admin-card-text)' }}>Buying Intent Signals</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflowY: 'auto', paddingRight: '4px' }}>
                    {session?.buyingIntentSnapshots?.filter(b => b.signals && b.signals.length > 0).map((b, i) => (
                      <div key={i} style={{ padding: '8px 10px', borderRadius: '0', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--admin-card-border)', fontSize: '12px' }}>
                        <span style={{ fontWeight: 700, color: '#F59E0B' }}>{b.label}: </span>
                        <span style={{ color: 'var(--admin-card-text)' }}>{b.signals?.join(', ')}</span>
                      </div>
                    ))}
                    {(!session?.buyingIntentSnapshots || session.buyingIntentSnapshots.filter(b => b.signals && b.signals.length > 0).length === 0) && (
                      <div style={{ fontSize: '12px', color: 'var(--admin-card-subtext)', opacity: 0.7 }}>No specific buying signals recorded.</div>
                    )}
                  </div>
                </div>

                <div style={{ padding: '16px', borderRadius: '0', border: '1px solid var(--admin-card-border)', background: 'var(--admin-card-bg)' }}>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '14px', fontWeight: 700, marginBottom: '12px', color: 'var(--admin-card-text)' }}>Objections Handled</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflowY: 'auto', paddingRight: '4px' }}>
                    {session?.objectionEvents?.map((obj, i) => (
                      <div key={i} style={{ padding: '8px 10px', borderRadius: '0', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--admin-card-border)', fontSize: '12px' }}>
                        <span style={{ fontWeight: 700, color: '#ef4444', textTransform: 'capitalize' }}>{obj.primaryCategory}: </span>
                        <span style={{ color: 'var(--admin-card-text)' }}>{obj.reasoning || 'Objection raised'}</span>
                      </div>
                    ))}
                    {(!session?.objectionEvents || session.objectionEvents.length === 0) && (
                      <div style={{ fontSize: '12px', color: 'var(--admin-card-subtext)', opacity: 0.7 }}>No major objections detected during call.</div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          ) : (
            /* Transcript Tab */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '500px', overflowY: 'auto', paddingRight: '8px' }}>
              {session?.transcriptSegments?.length ? session.transcriptSegments.map((seg, i) => (
                <div key={i} style={{ padding: '12px', borderRadius: '0', border: '1px solid var(--admin-card-border)', background: seg.isRep ? 'rgba(56, 189, 248, 0.03)' : 'rgba(255, 255, 255, 0.01)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '11px', fontWeight: 700 }}>
                    <span style={{ color: seg.isRep ? '#38bdf8' : '#10b981', fontFamily: "'Space Grotesk', sans-serif" }}>
                      {seg.isRep ? 'Sales Rep' : seg.speaker || 'Prospect'}
                    </span>
                    <span style={{ color: 'var(--admin-card-subtext)', fontWeight: 400 }}>{seg.recordedAt ? new Date(seg.recordedAt).toLocaleTimeString() : `Seq #${seg.sequenceNo}`}</span>
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--admin-card-text)', margin: 0, lineHeight: 1.4, fontFamily: "'Inter', sans-serif" }}>
                    {seg.text}
                  </p>
                </div>
              )) : (
                <div style={{ padding: '30px', textAlign: 'center', color: 'var(--admin-card-subtext)', fontSize: '13px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <MessageSquare size={24} style={{ opacity: 0.5 }} />
                  No transcript recorded for this session.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
